import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { Resource } from '@opentelemetry/resources';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';
import type { Tracer } from '@opentelemetry/api';
import { getGlobalProperties } from '../../global-properties/global-properties';
import { getAppName } from '../../app-name/app-name';
import { SessionAttributesProcessor } from './session-attributes-processor';

const DATADOG_TRACES_ENDPOINT = 'https://otlp.datadoghq.com/v1/traces';

let provider: WebTracerProvider | undefined;
let tracer: Tracer | undefined;
let isInitialized = false;

/**
 * Returns the OTel tracer instance, or undefined if OTel has not been initialized.
 * Used internally by create-span functions.
 */
export function getTracer(): Tracer | undefined {
    return tracer;
}

/**
 * Initialize the OpenTelemetry tracing client for browser.
 *
 * Creates a WebTracerProvider with a BatchSpanProcessor that sends spans
 * to Datadog's OTLP endpoint. The provider is stored in a module-level
 * variable and is NOT registered globally (to avoid conflicts with
 * Faro's global OTel provider).
 *
 * No-ops silently if:
 * - Already initialized
 * - datadogApiKey is not provided
 *
 * Should be called after setupDatadog() in the bootstrap flow.
 */
export async function setupOtel(opts: { datadogApiKey: string | undefined }): Promise<void> {
    if (isInitialized) {
        return;
    }

    const apiKey = opts.datadogApiKey;

    if (!apiKey) {
        return;
    }

    const globalProperties = await getGlobalProperties();
    const appName = getAppName();
    const serviceName = appName === 'cad' ? 'cad-fe' : 'fr-fe';
    const environment = globalProperties.ENVIRONMENT || 'unknown';
    const version = globalProperties.VERSION;

    const resource = new Resource({
        [ATTR_SERVICE_NAME]: serviceName,
        [ATTR_SERVICE_VERSION]: version,
        'deployment.environment': environment,
    });

    const traceExporter = new OTLPTraceExporter({
        url: DATADOG_TRACES_ENDPOINT,
        headers: {
            'dd-api-key': apiKey,
            'dd-otlp-source': 'datadog',
            'dd-otlp-service-name': serviceName,
        },
    });

    const batchProcessor = new BatchSpanProcessor(traceExporter);
    const sessionProcessor = new SessionAttributesProcessor();

    provider = new WebTracerProvider({
        resource,
        spanProcessors: [batchProcessor, sessionProcessor],
    });

    // Do NOT call provider.register() — Faro owns the global provider.
    // Instead, get a tracer directly from this provider.
    tracer = provider.getTracer(serviceName, version);
    isInitialized = true;
}
